import { CatalogItem, Office365Catalog, Office365Tab } from '../services/product-service';
import { O365ProductApiItem, O365ProductApiResponse } from '../models/o365-product.models';

const PRODUCT_ICON = '/assets/images/Rackspace_Email.svg';
const ADDON_ICON = '/assets/images/Rackspace_Email_archiving.svg';

export interface Office365TabLayout {
  name: string;
  signUpCategory: string;
  /** Fallback product order/assignment when signUpCategory is missing from the API. */
  productXmlAttributeNames?: string[];
}

export interface Office365LayoutConfig {
  tabs: Office365TabLayout[];
}

/** Maps API xmlAttributeName values to internal catalog productId keys. */
export function xmlAttributeNameToProductId(xmlAttributeName: string): string {
  if (xmlAttributeName === 'o365EmailArchiving') {
    return 'O365_EMAIL_ARCHIVING';
  }
  return xmlAttributeName.replace(/^o365/i, '').toUpperCase();
}

function mapProductWithAddons(apiProduct: O365ProductApiItem): CatalogItem {
  const product = mapApiItemToCatalogItem(apiProduct, PRODUCT_ICON);
  product.addons = (apiProduct.addons ?? []).map((apiAddon) => ({
    ...mapApiItemToCatalogItem(apiAddon, ADDON_ICON),
    parentProductId: product.productId,
    group: 'o365-addon',
  }));
  return product;
}

function toBillingCycle(value: string): 'monthly' | 'annual' {
  return value?.toLowerCase() === 'annual' ? 'annual' : 'monthly';
}

function mapApiItemToCatalogItem(item: O365ProductApiItem, icon: string): CatalogItem {
  return {
    offeringId: 'O365',
    productId: xmlAttributeNameToProductId(item.xmlAttributeName),
    offerId: item.offerId,
    externalOfferId: item.externalOfferId,
    serviceCode: item.serviceCode,
    xmlAttributeName: item.xmlAttributeName,
    quantity: '1',
    price: item.price,
    type: item.type,
    name: item.preferredName || item.productName,
    description: item.description,
    icon,
    billingCycle: toBillingCycle(item.billingCycle),
  };
}

function isAnnualCommitProduct(apiProduct: O365ProductApiItem): boolean {
  return apiProduct.commitment?.toLowerCase() === 'annual commit';
}

/** Annual SKUs linked to a monthly plan are attached as annualVariant, not listed separately. */
function hasMonthlyProductPeer(
  apiResponse: O365ProductApiResponse,
  apiProduct: O365ProductApiItem
): boolean {
  return apiResponse.o365Products.some(
    (candidate) =>
      candidate.type === 'product' &&
      !isAnnualCommitProduct(candidate) &&
      candidate.externalOfferId === apiProduct.externalOfferId
  );
}

function shouldListProductInTab(
  apiResponse: O365ProductApiResponse,
  apiProduct: O365ProductApiItem,
  tab: Office365TabLayout
): boolean {
  if (apiProduct.type !== 'product' || !productBelongsToTab(apiProduct, tab)) {
    return false;
  }

  if (isAnnualCommitProduct(apiProduct) && hasMonthlyProductPeer(apiResponse, apiProduct)) {
    return false;
  }

  return true;
}

function matchesSignUpCategory(apiProduct: O365ProductApiItem, signUpCategory: string): boolean {
  return apiProduct.signUpCategory?.toLowerCase() === signUpCategory.toLowerCase();
}

function productBelongsToTab(apiProduct: O365ProductApiItem, tab: Office365TabLayout): boolean {
  if (apiProduct.signUpCategory) {
    return matchesSignUpCategory(apiProduct, tab.signUpCategory);
  }

  return tab.productXmlAttributeNames?.includes(apiProduct.xmlAttributeName) ?? false;
}

function buildTabProduct(
  apiProduct: O365ProductApiItem,
  annualByExternalOfferId: Map<string, O365ProductApiItem>
): CatalogItem {
  const product = mapProductWithAddons(apiProduct);
  if (!isAnnualCommitProduct(apiProduct)) {
    const annualApiProduct = annualByExternalOfferId.get(apiProduct.externalOfferId);
    if (annualApiProduct) {
      product.annualVariant = mapProductWithAddons(annualApiProduct);
    }
  }
  return product;
}

function sortByXmlAttributeOrder(products: O365ProductApiItem[], order?: string[]): O365ProductApiItem[] {
  if (!order?.length) {
    return products;
  }

  const rank = new Map(order.map((xmlName, index) => [xmlName, index]));
  return [...products].sort((left, right) => {
    const leftRank = rank.get(left.xmlAttributeName) ?? Number.MAX_SAFE_INTEGER;
    const rightRank = rank.get(right.xmlAttributeName) ?? Number.MAX_SAFE_INTEGER;
    return leftRank - rightRank;
  });
}

export function buildOffice365Catalog(
  apiResponse: O365ProductApiResponse,
  layout: Office365LayoutConfig
): Office365Catalog {
  const productsByXml = new Map(
    apiResponse.o365Products.map((product) => [product.xmlAttributeName, product])
  );

  const annualByExternalOfferId = new Map<string, O365ProductApiItem>();
  for (const apiProduct of apiResponse.o365Products) {
    if (apiProduct.type === 'product' && isAnnualCommitProduct(apiProduct)) {
      annualByExternalOfferId.set(apiProduct.externalOfferId, apiProduct);
    }
  }

  const tabs: Office365Tab[] = layout.tabs.map((tab) => {
    const tabProducts = sortByXmlAttributeOrder(
      apiResponse.o365Products.filter((apiProduct) =>
        shouldListProductInTab(apiResponse, apiProduct, tab)
      ),
      tab.productXmlAttributeNames
    );

    // Preserve explicit tab order when API products lack signUpCategory.
    if (tab.productXmlAttributeNames?.length && tabProducts.length === 0) {
      return {
        name: tab.name,
        products: tab.productXmlAttributeNames
          .map((xmlName) => productsByXml.get(xmlName))
          .filter(
            (apiProduct): apiProduct is O365ProductApiItem =>
              !!apiProduct && shouldListProductInTab(apiResponse, apiProduct, tab)
          )
          .map((apiProduct) => buildTabProduct(apiProduct, annualByExternalOfferId)),
      };
    }

    return {
      name: tab.name,
      products: tabProducts.map((apiProduct) =>
        buildTabProduct(apiProduct, annualByExternalOfferId)
      ),
    };
  });

  const addons: CatalogItem[] = tabs.flatMap((tab) =>
    tab.products.flatMap((product) => product.addons ?? [])
  );

  return { tabs, addons };
}
